import { datadogRum } from '@datadog/browser-rum';
import { apm } from '@elastic/apm-rum';
import axios, { isAxiosError } from 'axios';
import posthog from 'posthog-js';
import { BehaviorSubject, Subject } from 'rxjs';
import { Auth0IDTokenProvider } from './id-token-provider.service';
// 1 hour
const TOKEN_REFRESH_INTERVAL = 1000 * 60 * 60;
export class AuthService {
    // FIXME: emitting separate events for different state values causes app fragility. Components are rendered twice in a desynced state. All the states should be emitted together since the app makes decisions based in sync data. If downstream needs it it can filter the observables to the event it needs.
    static currentAuthoritySubject = new BehaviorSubject(null);
    static appAdminSubject = new BehaviorSubject(false);
    static signedOutSubject = new Subject();
    static appAdmin = false;
    static betaTesterSubject = new BehaviorSubject(false);
    static betaTester = false;
    static currentToken = '';
    static authorities = [];
    static authLoadingSubject = new BehaviorSubject(false);
    static config;
    static currentIDToken = null;
    static tokenRefreshTimer = null;
    static idTokenProvider;
    static signOutHandler;
    constructor(axiosInstances, idTokenProvider, clientOptions) {
        if (!clientOptions) {
            throw new Error('Missing auth ENVs');
        }
        AuthService.config = clientOptions;
        if (clientOptions.settings.defaultApp === 'wayste-dashboard') {
            console.warn('No Authentication for wayste-dashboard');
            return;
        }
        AuthService.idTokenProvider = idTokenProvider;
        AuthService.currentAuthoritySubject.subscribe((authority) => {
            if (authority) {
                AuthService.currentToken = authority.token;
                axiosInstances.mainInstance.defaults.headers.common['Authorization'] = `Bearer ${authority.token}`;
                axiosInstances.socket.setToken(`Bearer ${authority.token}`);
                if (apm.isActive()) {
                    apm.setUserContext({
                        id: authority.profileID,
                    });
                }
            }
            else {
                delete axiosInstances.mainInstance.defaults.headers.common['Authorization'];
                axiosInstances.socket.setToken(null);
                if (apm.isActive()) {
                    apm.setUserContext({});
                }
            }
        });
        AuthService.appAdminSubject.subscribe((authority) => {
            AuthService.appAdmin = authority;
            if (authority) {
                axiosInstances.appAdminInstance.defaults.headers.common['Authorization'] = `Bearer ${authority.token}`;
            }
            else {
                delete axiosInstances.appAdminInstance.defaults.headers.common['Authorization'];
            }
        });
        AuthService.betaTesterSubject.subscribe((betaTester) => {
            AuthService.betaTester = betaTester;
        });
    }
    registerAuthLifecycleHandlers({ tokenAccessors, signOutHandler, }) {
        if (AuthService.idTokenProvider instanceof Auth0IDTokenProvider) {
            AuthService.idTokenProvider.registerTokenAccessors(tokenAccessors);
            AuthService.signOutHandler = signOutHandler;
            this.authSetup();
        }
    }
    // auth flow
    // (1) check local cache for an ID token if it exists --> go to (3)
    // (2) user signs in with credentials --> send to identity provider --> get ID token back
    // (3) make request to profile service to getAvailableAuthorityTokens service which has internal auth token
    async authSetup() {
        AuthService.authLoadingSubject.next(true);
        // (1) check local cache for ID token if it exists
        try {
            const idToken = await AuthService.idTokenProvider.fetchRaw();
            if (!idToken) {
                throw new Error('No auth token found');
            }
            AuthService.currentIDToken = idToken;
            const authorities = await this.fetchAvailableAuthorityTokens(idToken);
            AuthService.authorities = authorities;
            const filteredAuthorities = this.filterTokensByAppAndRole(authorities);
            if (filteredAuthorities.length > 0) {
                AuthService.currentAuthoritySubject.next(filteredAuthorities[0]);
                AuthService.tokenRefreshTimer = window.setTimeout(() => {
                    this.refreshAuth();
                }, TOKEN_REFRESH_INTERVAL);
                AuthService.authLoadingSubject.next(false);
                return;
            }
            else {
                throw new Error('No authorities found, you are not authorized to use this app. If you believe this is an error, please contact support.');
            }
        }
        catch (error) {
            console.error(error);
            posthog.captureException(error);
            this.signOut();
            // AuthService.currentAuthoritySubject.next(null); removed because emitting null twice causes issues
            AuthService.authLoadingSubject.next(false);
            console.warn('Token is not valid or does not exist, back to login', error);
        }
        AuthService.authLoadingSubject.next(false);
    }
    async refreshAuth() {
        if (AuthService.tokenRefreshTimer) {
            clearTimeout(AuthService.tokenRefreshTimer);
        }
        // (1) check local cache for an ID token if it exists
        try {
            const token = await AuthService.idTokenProvider.fetchRaw();
            if (!token) {
                throw new Error('No session');
            }
            AuthService.currentIDToken = token;
            const authorities = await this.fetchAvailableAuthorityTokens(token);
            AuthService.authorities = authorities;
            const filteredAuthorities = this.filterTokensByAppAndRole(authorities);
            if (filteredAuthorities.length > 0) {
                AuthService.currentAuthoritySubject.next(filteredAuthorities[0]);
                AuthService.tokenRefreshTimer = window.setTimeout(() => {
                    this.refreshAuth();
                }, TOKEN_REFRESH_INTERVAL);
            }
            else {
                throw new Error('No authorities found, you are not authorized to use this app. If you believe this is an error, please contact support.');
            }
        }
        catch (error) {
            console.error(error);
            posthog.captureException(error);
            this.signOut();
            console.warn('Token is not valid or does not exist, back to login', error);
        }
    }
    get currentAuthority() {
        return AuthService.currentAuthoritySubject.asObservable();
    }
    get signedOut() {
        return AuthService.signedOutSubject.asObservable();
    }
    get currentToken() {
        return AuthService.currentToken;
    }
    get getCurrentIDToken() {
        return AuthService.currentIDToken;
    }
    get loading() {
        return AuthService.authLoadingSubject.asObservable();
    }
    get appAdmin() {
        return AuthService.appAdminSubject.asObservable();
    }
    get betaTester() {
        return AuthService.betaTesterSubject.asObservable();
    }
    get isBetaTester() {
        return AuthService.betaTester;
    }
    get currentAppAdmin() {
        return AuthService.appAdmin;
    }
    async signUp({ email }) {
        if (AuthService.config.settings.defaultApp !== 'customer-portal') {
            throw Error('Not implemented');
        }
        try {
            const res = await axios.post(AuthService.config.API.baseApiUrl + '/profile/customer/signup', {
                email,
            });
            return res.data;
        }
        catch (error) {
            if (isAxiosError(error) && error.response?.status === 409) {
                // no point in logging this error to error tracking
                throw error;
            }
            datadogRum.setUser({ email });
            posthog.captureException(error);
            datadogRum.clearUser();
            throw error;
        }
    }
    async signOut() {
        try {
            await AuthService.signOutHandler?.();
            AuthService.signedOutSubject.next();
            // do sign out stuff then
            AuthService.authorities = [];
            AuthService.currentAuthoritySubject.next(null);
            AuthService.appAdminSubject.next(false);
        }
        catch (error) {
            console.error('Error signing out', error);
            posthog.captureException(error);
        }
    }
    getAuthorities() {
        return AuthService.authorities;
    }
    getAvailableRoles() {
        return AuthService.authorities.map((authority) => {
            return authority.roles[0];
        });
    }
    filterTokensByAppAndRole(authorities) {
        // first filter by app
        const filteredAuthorities = authorities.filter((authority) => {
            return authority.application === AuthService.config.settings.defaultApp;
        });
        // then filter by role
        if (AuthService.config.settings.defaultRole) {
            const filteredByRole = filteredAuthorities.filter((authority) => {
                // we just checked this above, but typescript doesn't know that :D
                return authority.roles.includes(AuthService.config.settings.defaultRole);
            });
            if (filteredByRole.length > 0) {
                return filteredByRole;
            }
        }
        return filteredAuthorities;
    }
    async fetchAvailableAuthorityTokens(idToken) {
        try {
            const response = await axios.post(AuthService.config.API.baseApiUrl + '/profile/auth', {
                headers: {
                    'Cache-Control': 'no-cache',
                    Pragma: 'no-cache',
                    Expires: '0',
                },
                idToken,
            });
            const authorities = response.data;
            if (authorities) {
                AuthService.authorities = authorities;
                // check if any or the authorities are BETA_TESTER
                const betaTesterAuthorities = authorities.filter((authority) => {
                    return authority.roles.includes('BETA_TESTER');
                });
                if (betaTesterAuthorities.length > 0) {
                    AuthService.betaTesterSubject.next(true);
                }
                else {
                    AuthService.betaTesterSubject.next(false);
                }
                // check if any or the authorities are APP_ADMIN
                const appAdminAuthorities = authorities.filter((authority) => {
                    return authority.roles.includes('APP_ADMIN');
                });
                if (appAdminAuthorities.length > 0) {
                    AuthService.appAdminSubject.next(appAdminAuthorities[0]);
                }
            }
            return authorities;
        }
        catch (error) {
            console.error('Failed to fetch authority tokens', error);
            posthog.captureException(error);
            await this.signOut();
            throw new Error('No tokens found, something likely went wrong when creating your user. Please contact support.');
        }
    }
}
//# sourceMappingURL=auth.service.js.map